import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useEffect, useRef } from 'react'
import {
  geoOrthographic,
  geoPath,
  geoGraticule10,
  geoInterpolate,
  geoDistance,
} from 'd3-geo'
import { feature, mesh } from 'topojson-client'
import type { Topology } from 'topojson-specification'
import worldData from 'world-atlas/countries-110m.json'
import { fontSans } from './theme'

interface GeoPoint {
  name: string
  region: string
  coords: [number, number] // [lon, lat]
  color: string
  labelDx: number
  labelDy: number
  anchor: 'start' | 'middle' | 'end'
}

// Austin, Dubai, Bengaluru, Shivamogga
const LOCATIONS: GeoPoint[] = [
  {
    name: 'Austin',
    region: 'TEXAS, USA',
    coords: [-97.74, 30.27],
    color: '#4A90E2',
    labelDx: 0,
    labelDy: -48,
    anchor: 'middle',
  },
  {
    name: 'Dubai',
    region: 'MIDDLE EAST',
    coords: [55.27, 25.2],
    color: '#4A90E2',
    labelDx: 0,
    labelDy: -48,
    anchor: 'middle',
  },
  {
    name: 'Bengaluru',
    region: 'INDIA',
    coords: [77.59, 12.97],
    color: '#9D4EDD',
    labelDx: -18,
    labelDy: -15,
    anchor: 'end',
  },
  {
    name: 'Shivamogga',
    region: 'INDIA',
    coords: [75.14, 13.93],
    color: '#9D4EDD',
    labelDx: 18,
    labelDy: 16,
    anchor: 'start',
  },
]

const ARCS: Array<{ from: [number, number]; to: [number, number] }> = [
  { from: LOCATIONS[0].coords, to: LOCATIONS[1].coords },
  { from: LOCATIONS[1].coords, to: LOCATIONS[2].coords },
  { from: LOCATIONS[2].coords, to: LOCATIONS[3].coords },
]

const W = 640
const H = 560
const R = 228

const smoothstep = (x: number, edge0: number, edge1: number) => {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

function GlobeMap() {
  const svgRef = useRef<SVGSVGElement>(null)
  const landRef = useRef<SVGPathElement>(null)
  const bordersRef = useRef<SVGPathElement>(null)
  const graticuleRef = useRef<SVGPathElement>(null)
  const arcRefs = useRef<Array<SVGPathElement | null>>([])
  const packetRefs = useRef<Array<SVGCircleElement | null>>([])
  const pinRefs = useRef<Array<SVGGElement | null>>([])

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return

    const world = worldData as unknown as Topology<{ countries: never }>
    const countries = world.objects.countries
    const land = feature(world, countries)
    const borders = mesh(world, countries, (a, b) => a !== b)
    const graticule = geoGraticule10()

    const projection = geoOrthographic()
      .scale(R)
      .translate([W / 2, H / 2])
      .rotate([-75, -12])
    const pathGen = geoPath(projection)

    const rot = { lambda: -75, phi: -12 }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let dragging = false
    let lastX = 0
    let lastY = 0
    let lastInteract = 0
    let lastDraw = 0
    let raf = 0
    let visibilityCleanup: (() => void) | null = null

    const onPointerDown = (e: PointerEvent) => {
      dragging = true
      lastX = e.clientX
      lastY = e.clientY
      lastInteract = performance.now()
      svg.setPointerCapture(e.pointerId)
      svg.style.cursor = 'grabbing'
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      lastX = e.clientX
      lastY = e.clientY
      rot.lambda += dx * 0.3
      rot.phi = Math.max(-58, Math.min(58, rot.phi - dy * 0.25))
      lastInteract = performance.now()
    }
    const endDrag = (e: PointerEvent) => {
      if (!dragging) return
      dragging = false
      lastInteract = performance.now()
      try {
        svg.releasePointerCapture(e.pointerId)
      } catch {
        // already released
      }
      svg.style.cursor = 'grab'
    }
    svg.addEventListener('pointerdown', onPointerDown)
    svg.addEventListener('pointermove', onPointerMove)
    svg.addEventListener('pointerup', endDrag)
    svg.addEventListener('pointercancel', endDrag)

    const draw = () => {
      const now = performance.now()
      // Throttle to ~12fps: SVG attribute writes invalidate layout each
      // frame, and a 60fps rotation storm starves input dispatch on
      // memory-constrained renderers. The drift stays smooth enough.
      if (now - lastDraw < 80) {
        raf = requestAnimationFrame(draw)
        return
      }
      lastDraw = now
      if (!dragging && !reduced && now - lastInteract > 2500) {
        rot.lambda -= 0.16 * 5 // compensate for ~5 skipped frames per tick
      }
      projection.rotate([rot.lambda, rot.phi])

      if (landRef.current)
        landRef.current.setAttribute('d', pathGen(land) || '')
      if (bordersRef.current)
        bordersRef.current.setAttribute('d', pathGen(borders) || '')
      if (graticuleRef.current)
        graticuleRef.current.setAttribute('d', pathGen(graticule as never) || '')

      const center: [number, number] = [-rot.lambda, -rot.phi]
      const visibility = LOCATIONS.map((loc) =>
        reduced
          ? 1
          : 1 - smoothstep(geoDistance(loc.coords, center), 1.3, 1.52),
      )

      LOCATIONS.forEach((loc, i) => {
        const g = pinRefs.current[i]
        if (!g) return
        const p = projection(loc.coords)
        if (!p) {
          g.setAttribute('opacity', '0')
          return
        }
        g.setAttribute('transform', `translate(${p[0]},${p[1]})`)
        g.setAttribute('opacity', String(visibility[i]))
      })

      ARCS.forEach((arc, i) => {
        const pathEl = arcRefs.current[i]
        const packet = packetRefs.current[i]
        if (!pathEl) return

        const interp = geoInterpolate(arc.from, arc.to)
        const N = 72
        let d = ''
        let pen = false
        for (let k = 0; k <= N; k++) {
          const t = k / N
          const c = interp(t)
          const visible = geoDistance(c, center) < Math.PI / 2 - 0.03
          if (visible) {
            const p = projection(c)
            if (p) {
              d += `${pen ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`
              pen = true
              continue
            }
          }
          pen = false
        }
        pathEl.setAttribute('d', d)

        const packetVisible =
          !reduced &&
          visibility[0] + visibility[1] > 0.4 /* at least one city visible */
        if (packet) {
          const t = (now / 5200 + i * 0.33) % 1
          const c = interp(t)
          if (packetVisible && geoDistance(c, center) < Math.PI / 2 - 0.03) {
            const p = projection(c)
            if (p) {
              packet.setAttribute('cx', String(p[0]))
              packet.setAttribute('cy', String(p[1]))
              packet.setAttribute('opacity', '0.9')
            } else {
              packet.setAttribute('opacity', '0')
            }
          } else {
            packet.setAttribute('opacity', '0')
          }
        }
      })

      raf = requestAnimationFrame(draw)
    }

    // Only animate while the globe is on screen — this page has more than
    // one animation loop, and embedded/weak devices choke when they all run
    // forever. Also draw one static frame up front so the globe is never blank.
    const start = () => {
      if (!raf) raf = requestAnimationFrame(draw)
    }
    const stop = () => {
      cancelAnimationFrame(raf)
      raf = 0
    }
    if (reduced) {
      draw()
      cancelAnimationFrame(raf)
      raf = 0
    } else {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
        { threshold: 0.05 },
      )
      io.observe(svg)
      const onVis = () => (document.hidden ? stop() : start())
      document.addEventListener('visibilitychange', onVis)
      visibilityCleanup = () => {
        document.removeEventListener('visibilitychange', onVis)
        io.disconnect()
      }
    }

    return () => {
      cancelAnimationFrame(raf)
      if (visibilityCleanup) visibilityCleanup()
      svg.removeEventListener('pointerdown', onPointerDown)
      svg.removeEventListener('pointermove', onPointerMove)
      svg.removeEventListener('pointerup', endDrag)
      svg.removeEventListener('pointercancel', endDrag)
    }
  }, [])

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '20px',
        overflow: 'hidden',
        background:
          'linear-gradient(160deg, #0a0f1d 0%, #0e1526 50%, #0a0f1d 100%)',
        border: '1px solid rgba(74, 144, 226, 0.14)',
        padding: '1rem 1rem 1.25rem',
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: '100%',
          display: 'block',
          cursor: 'grab',
          touchAction: 'pan-y',
        }}
        role="img"
        aria-label="Interactive globe showing Synkrasis.ai locations: San Francisco, Dubai, Bengaluru, Shivamogga"
      >
        <defs>
          <radialGradient id="globeOcean" cx="38%" cy="30%" r="78%">
            <stop offset="0%" stopColor="#16264a" />
            <stop offset="55%" stopColor="#0f1a35" />
            <stop offset="100%" stopColor="#0a1124" />
          </radialGradient>
          <linearGradient id="globeLand" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3c68a3" />
            <stop offset="50%" stopColor="#2d5286" />
            <stop offset="100%" stopColor="#3a5f92" />
          </linearGradient>
          <radialGradient id="globeAtmosphere" cx="50%" cy="50%" r="50%">
            <stop offset="72%" stopColor="rgba(74,144,226,0)" />
            <stop offset="86%" stopColor="rgba(74,144,226,0.16)" />
            <stop offset="100%" stopColor="rgba(74,144,226,0)" />
          </radialGradient>
          <radialGradient id="globeRimShade" cx="50%" cy="50%" r="50%">
            <stop offset="82%" stopColor="rgba(4,7,16,0)" />
            <stop offset="100%" stopColor="rgba(4,7,16,0.5)" />
          </radialGradient>
          <filter id="globePinGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="globePacketGlow" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="2.8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* atmosphere halo */}
        <circle cx={W / 2} cy={H / 2} r={R + 30} fill="url(#globeAtmosphere)" />
        <circle
          cx={W / 2}
          cy={H / 2}
          r={R + 12}
          fill="none"
          stroke="rgba(96,160,235,0.14)"
          strokeWidth="6"
        />

        {/* ocean sphere */}
        <circle
          cx={W / 2}
          cy={H / 2}
          r={R}
          fill="url(#globeOcean)"
          stroke="rgba(96,160,235,0.4)"
          strokeWidth="1.1"
        />

        {/* graticule */}
        <path
          ref={graticuleRef}
          fill="none"
          stroke="rgba(110,165,235,0.09)"
          strokeWidth="0.6"
        />

        {/* land */}
        <path
          ref={landRef}
          fill="url(#globeLand)"
          stroke="rgba(150,195,250,0.45)"
          strokeWidth="0.5"
        />

        {/* country borders */}
        <path
          ref={bordersRef}
          fill="none"
          stroke="rgba(140,185,245,0.28)"
          strokeWidth="0.5"
        />

        {/* limb darkening for depth */}
        <circle cx={W / 2} cy={H / 2} r={R} fill="url(#globeRimShade)" pointerEvents="none" />

        {/* connection arcs */}
        {ARCS.map((_, i) => (
          <path
            key={`arc-${i}`}
            ref={(el) => {
              arcRefs.current[i] = el
            }}
            fill="none"
            stroke="rgba(140,195,255,0.72)"
            strokeWidth="1.4"
            strokeDasharray="5 4"
          />
        ))}

        {/* travelling packets */}
        {ARCS.map((_, i) => (
          <circle
            key={`packet-${i}`}
            ref={(el) => {
              packetRefs.current[i] = el
            }}
            r="3.4"
            fill="#cfe6ff"
            filter="url(#globePacketGlow)"
            opacity="0"
          />
        ))}

        {/* location pins with chip labels */}
        {LOCATIONS.map((loc, i) => {
          const chipW =
            Math.round(Math.max(loc.name.length * 6.9, loc.region.length * 5.9)) + 16
          const chipX =
            loc.anchor === 'end' ? -chipW - 4 : loc.anchor === 'start' ? 4 : -chipW / 2
          return (
            <g
              key={loc.name}
              ref={(el) => {
                pinRefs.current[i] = el
              }}
              style={{ transition: 'opacity 0.4s ease' }}
            >
              <g filter="url(#globePinGlow)">
                <circle
                  className="globe-pin-pulse"
                  r="9"
                  fill="none"
                  stroke={loc.color}
                  strokeWidth="1.3"
                />
                <circle r="5" fill={loc.color} />
                <circle cx="-1.4" cy="-1.4" r="1.6" fill="rgba(255,255,255,0.8)" />
              </g>
              <g transform={`translate(${loc.labelDx},${loc.labelDy})`}>
                <rect
                  x={chipX}
                  y={0}
                  width={chipW}
                  height={31}
                  rx={7}
                  fill="rgba(6,10,21,0.86)"
                  stroke="rgba(140,180,240,0.28)"
                  strokeWidth="0.8"
                />
                <text
                  x={chipX + chipW / 2}
                  y={13}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.96)"
                  fontSize="11"
                  fontWeight="700"
                  fontFamily={fontSans}
                >
                  {loc.name}
                </text>
                <text
                  x={chipX + chipW / 2}
                  y={24}
                  textAnchor="middle"
                  fill={loc.color}
                  fontSize="7.5"
                  fontWeight="600"
                  letterSpacing="0.1em"
                  fontFamily={fontSans}
                >
                  {loc.region}
                </text>
              </g>
            </g>
          )
        })}
      </svg>

      {/* Location pills */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.6rem',
          marginTop: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        {LOCATIONS.map((loc) => (
          <div
            key={loc.name}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              background: `${loc.color}12`,
              border: `1px solid ${loc.color}30`,
              fontFamily: fontSans,
              fontSize: '0.7rem',
              fontWeight: 600,
              color: loc.color,
              letterSpacing: '0.02em',
            }}
          >
            {loc.name}
          </div>
        ))}
      </div>

      <div
        style={{
          position: 'absolute',
          top: '1.1rem',
          right: '1.25rem',
          fontFamily: fontSans,
          fontSize: '0.64rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          pointerEvents: 'none',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
          <path
            d="M2 8h12M8 2v12"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            opacity="0.7"
          />
        </svg>
        Drag to explore
      </div>

      <style>{`
        .globe-pin-pulse {
          transform-box: fill-box;
          transform-origin: center;
          animation: globePinPulse 2.6s ease-out infinite;
        }
        @keyframes globePinPulse {
          0% { transform: scale(0.5); opacity: 0.9; }
          100% { transform: scale(2.4); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .globe-pin-pulse { animation: none; opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export function About() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  const stats = [
    { val: '20+', label: 'Years Experience' },
    { val: '3', label: 'Global Regions' },
    { val: '24/7', label: 'Global Coverage' },
  ]

  return (
    <section
      id="company"
      style={{
        padding: '7rem 2rem',
        background: 'linear-gradient(180deg, #0A0F1F 0%, #0c1222 100%)',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(157, 78, 221, 0.3), transparent)',
        }}
      />

      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '4rem',
            alignItems: 'center',
          }}
        >
          {/* Text side */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#9D4EDD',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              About Us
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '1.5rem',
              }}
            >
              Decades of Experience.{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                A Future-Ready Vision.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.75,
                marginBottom: '2.5rem',
              }}
            >
              Synkrasis.ai is built by a team with over 20 years of industry
              experience in data engineering and analytics. We've felt the pain
              of legacy systems — and built the cure. We are a global team,
              headquartered with operations in the{' '}
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                USA, Dubai, and India
              </span>
              , ready to serve you 24/7.
            </p>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
              {stats.map((s) => (
                <div key={s.label}>
                  <div
                    style={{
                      fontFamily: fontSans,
                      fontSize: '2rem',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      lineHeight: 1,
                    }}
                  >
                    {s.val}
                  </div>
                  <div
                    style={{
                      fontFamily: fontSans,
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.4)',
                      marginTop: '4px',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Globe side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <GlobeMap />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
